'use client';

import { AccessGuard } from '@ps/features/access-control/components/access-guard/access-guard';
import type { CreatorInfo } from '@ps/types/creator';

// ─── BookButton ───────────────────────────────────────────────────────────
// Pure presentation button.

export function BookButton({
  disabled = false,
  loading = false,
  compact = false,
  onPress,
}: {
  disabled?: boolean;
  loading?: boolean;
  compact?: boolean;
  onPress?: () => void;
}) {
  return (
    <div className={compact ? 'mt-1' : 'mt-4'}>
      <button
        type="button"
        onClick={onPress}
        disabled={disabled || loading}
        className={`block w-full rounded-lg text-center font-semibold transition-colors ${
          compact ? 'py-1.5 text-[10px]' : 'py-3 text-sm'
        } ${
          disabled
            ? 'force-light:bg-gray-200 force-light:text-gray-400 cursor-not-allowed bg-gray-200 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
            : 'bg-mint-green-300 force-light:disabled:bg-gray-300 text-black disabled:bg-gray-300 disabled:dark:bg-gray-600'
        }`}
      >
        {loading ? 'Booking...' : 'Book'}
      </button>
    </div>
  );
}

// ─── WiredBookButton ──────────────────────────────────────────────────────
// Wraps BookButton with access guard (follow / lead form).
// Mirrors WiredBookMeetingButtonV2 from RN.

export function WiredBookButton({
  storeId,
  triggers,
  sellerProfile,
  skipFollowGuard,
  isBooking,
  compact = false,
  onBook,
}: {
  storeId: string;
  triggers: Array<{ type: string; id: string }>;
  sellerProfile: CreatorInfo;
  skipFollowGuard?: boolean;
  isBooking: boolean;
  compact?: boolean;
  onBook: () => void;
}) {
  return (
    <AccessGuard
      storeId={storeId}
      guard={{
        kind: skipFollowGuard ? 'skip-follow' : 'follow',
        triggers,
      }}
      sellerProfile={sellerProfile}
      onAccessGranted={onBook}
    >
      {(accessGranted, requestAccess, isReady) => (
        <BookButton
          disabled={!isReady}
          loading={isBooking}
          compact={compact}
          onPress={() => {
            if (!accessGranted) {
              requestAccess();
              return;
            }
            onBook();
          }}
        />
      )}
    </AccessGuard>
  );
}
